import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import qs from 'qs'
import { showSectionSelectionModal } from '../../actions/modals'
import SearchProviderContainer from './SearchProviderContainer'
import Button from '../../components/uiKit/Button'
import TopPageHeader from '../../components/pageHeaders/TopPageHeader'
import StickyPageHeader from '../../components/pageHeaders/StickyPageHeader'
import SearchResultsCard from '../../components/search/SearchResultsCard'
import SearchResultsFolderCard from '../../components/search/SearchResultsFolderCard'
import SearchResultsRepoCard from '../../components/search/SearchResultsRepoCard'
import CategoryFilters from '../../components/search/CategoryFilters'
import Icon from '../../components/misc/Icons'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getHitKind } from '../../utils/search/getHitKind'
import { useSearchContext } from '../../services/search/SearchContext'
import { useSupplementarySearch } from '../../services/search/SupplementarySearchContext'
import analytics from '../../utils/cdpAnalytics'

const updateAfter = 700
const createURL = (state) => `?${qs.stringify({ query: state.query })}`
const searchStateToUrl = (props, searchState) => (
  searchState ? `${props.location.pathname}${createURL(searchState)}` : ''
)
const urlToSearchState = (location) => qs.parse(location.search.slice(1))

// ─── Search input ──────────────────────────────────────────────────────────────

function SearchInput() {
  const { currentRefinement, refine } = useSearchContext()
  return (
    <input
      type='text'
      placeholder='Search'
      value={currentRefinement}
      onChange={(e) => refine(e.target.value)}
      autoComplete='off'
      className='topBar-search'
      id='searchInputField'
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          document.getElementById('searchInputField').blur()
        }
      }}
    />
  )
}

// ─── Router history bridge (react-router v4 — no useHistory) ──────────────────

const RouterHistoryContext = React.createContext({ history: { push: () => {} } })
const RouterHistoryBridge = withRouter(({ history, children }) => (
  <RouterHistoryContext.Provider value={{ history }}>
    {children}
  </RouterHistoryContext.Provider>
))
function useRouterHistory() {
  return React.useContext(RouterHistoryContext)
}

// ─── Category-filter context ──────────────────────────────────────────────────
// Lives on SearchResultsContainer's state. Descendants (the header pill bar
// and the per-category groups) subscribe via useCategoryFilters().

const DEFAULT_CATEGORY_FILTERS = {
  enabled: {
    repos: true, folders: true, sets: true, questions: true,
  },
  onToggle: () => {},
}
const CategoryFiltersContext = React.createContext(DEFAULT_CATEGORY_FILTERS)
function useCategoryFilters() {
  return React.useContext(CategoryFiltersContext)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getAdditiveBlock(additiveResults, providerId) {
  if (!Array.isArray(additiveResults)) return null
  return additiveResults.find((r) => r.providerId === providerId) || null
}

function getAdditiveHits(additiveResults, providerId) {
  const block = getAdditiveBlock(additiveResults, providerId)
  return (block && Array.isArray(block.hits)) ? block.hits : []
}

function getLocalIndexResult(additiveResults, providerId, indexName) {
  const block = getAdditiveBlock(additiveResults, providerId)
  const ir = block && block.indexResults
  if (!ir) return null
  return ir[indexName] || null
}

// ─── Section group header (Library / Navigation / Help) ───────────────────────
// Rendered like the dropdown sectionHeader: icon on the left, uppercase label.

function SectionGroupHeader({ icon, label }) {
  return (
    <div className='searchResults-sectionGroupHeader'>
      <div className='searchResults-sectionGroupHeader-icon'>
        <Icon name={icon} />
      </div>
      <span className='searchResults-sectionGroupHeader-label'>{label}</span>
    </div>
  )
}

// ─── Category header within Library (PACKS, FOLDERS, SETS, QUESTIONS) ─────────

function CategoryHeader({ label }) {
  return <div className='searchResults-categoryHeader'>{label}</div>
}

// ─── Source indicator (Algolia / Local·Fuse / Local·BM25) ─────────────────────

function SourceLabel({ label }) {
  return <div className='searchResults-sourceLabel'>{label}</div>
}

// ─── Individual hit renderer ───────────────────────────────────────────────────

const Hit = withRouter(({ item, play }) => {
  const kind = getHitKind(item)
  if (kind === 'set') {
    return (
      <SearchResultsCard
        set
        item={item}
        title={item.name}
        play={() => { play(item) }}
      />
    )
  }
  if (kind === 'question') {
    return (
      <SearchResultsCard
        question
        item={item}
        title={getQuestionBodyLabel(item)}
        play={() => { play(item) }}
      />
    )
  }
  if (kind === 'folder') {
    return <SearchResultsFolderCard folder={item} />
  }
  if (kind === 'repo') {
    return <SearchResultsRepoCard repo={item} />
  }
  return null
})

function HitList({ hits, prefix, play }) {
  if (!hits || hits.length === 0) return null
  return (
    <div className='searchResults-list'>
      {hits.map((hit) => (
        <Hit
          item={hit}
          key={prefix ? `${prefix}-${hit.objectID}` : hit.objectID}
          play={play}
        />
      ))}
    </div>
  )
}

// ─── Generic grouped library category ─────────────────────────────────────────
// Renders one category (e.g. "PACKS") showing Algolia results first, then
// Fuse, then BM25. Source labels are only shown when more than one source
// has results (avoids noise when only one engine matched).

function LibraryCategory({
  label,
  algoliaHits,
  algoliaHasMore,
  algoliaLoadMore,
  fuseHits,
  bm25Hits,
  play,
}) {
  const { currentRefinement } = useSearchContext()
  const total = algoliaHits.length + fuseHits.length + bm25Hits.length
  if (!currentRefinement || total === 0) return null
  const activeSources = [algoliaHits, fuseHits, bm25Hits].filter((h) => h.length > 0).length
  const multiSource = activeSources > 1
  return (
    <div className='searchResults-categoryGroup'>
      <CategoryHeader label={label} />
      {algoliaHits.length > 0 && (
        <React.Fragment>
          {multiSource && <SourceLabel label='Algolia' />}
          <HitList hits={algoliaHits} play={play} />
          {algoliaHasMore && (
            <div className='searchResults-list-showMoreContainer'>
              <Button label='Show more results' size='large' onClickFunction={algoliaLoadMore} />
            </div>
          )}
        </React.Fragment>
      )}
      {fuseHits.length > 0 && (
        <React.Fragment>
          <SourceLabel label='Local · Fuse' />
          <HitList hits={fuseHits} prefix='fuse' play={play} />
        </React.Fragment>
      )}
      {bm25Hits.length > 0 && (
        <React.Fragment>
          <SourceLabel label='Local · BM25' />
          <HitList hits={bm25Hits} prefix='bm25' play={play} />
        </React.Fragment>
      )}
    </div>
  )
}

// ─── Per-category wrappers ─────────────────────────────────────────────────────

function PacksGroup({ play }) {
  const { indexResults } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const algo = indexResults.repos || {}
  const fuseR = getLocalIndexResult(additiveResults, 'local-content', 'repos') || {}
  const bm25R = getLocalIndexResult(additiveResults, 'minisearch-content', 'repos') || {}
  return (
    <LibraryCategory
      label='Packs'
      algoliaHits={algo.hits || []}
      algoliaHasMore={!!algo.hasMore}
      algoliaLoadMore={algo.loadMore}
      fuseHits={fuseR.hits || []}
      bm25Hits={bm25R.hits || []}
      play={play}
    />
  )
}

function FoldersGroup({ play }) {
  const { indexResults } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const algo = indexResults.folders || {}
  const fuseR = getLocalIndexResult(additiveResults, 'local-content', 'folders') || {}
  const bm25R = getLocalIndexResult(additiveResults, 'minisearch-content', 'folders') || {}
  return (
    <LibraryCategory
      label='Folders'
      algoliaHits={algo.hits || []}
      algoliaHasMore={!!algo.hasMore}
      algoliaLoadMore={algo.loadMore}
      fuseHits={fuseR.hits || []}
      bm25Hits={bm25R.hits || []}
      play={play}
    />
  )
}

function SetsGroup({ play }) {
  const { indexResults } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const algo = indexResults.sets_questions || {}
  const fuseR = getLocalIndexResult(additiveResults, 'local-content', 'sets_questions') || {}
  const bm25R = getLocalIndexResult(additiveResults, 'minisearch-content', 'sets_questions') || {}
  const isSet = (h) => getHitKind(h) === 'set'
  return (
    <LibraryCategory
      label='Sets'
      algoliaHits={(algo.hits || []).filter(isSet)}
      algoliaHasMore={false}
      algoliaLoadMore={null}
      fuseHits={(fuseR.hits || []).filter(isSet)}
      bm25Hits={(bm25R.hits || []).filter(isSet)}
      play={play}
    />
  )
}

function QuestionsGroup({ play }) {
  const { indexResults } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const algo = indexResults.sets_questions || {}
  const fuseR = getLocalIndexResult(additiveResults, 'local-content', 'sets_questions') || {}
  const bm25R = getLocalIndexResult(additiveResults, 'minisearch-content', 'sets_questions') || {}
  const isQuestion = (h) => getHitKind(h) === 'question'
  return (
    <LibraryCategory
      label='Questions'
      algoliaHits={(algo.hits || []).filter(isQuestion)}
      algoliaHasMore={false}
      algoliaLoadMore={null}
      fuseHits={(fuseR.hits || []).filter(isQuestion)}
      bm25Hits={(bm25R.hits || []).filter(isQuestion)}
      play={play}
    />
  )
}

// ─── Library section ───────────────────────────────────────────────────────────

function LibrarySection({ play }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const { enabled } = useCategoryFilters()
  const algoliaAny = (
    (indexResults.repos?.nbHits || 0) +
    (indexResults.folders?.nbHits || 0) +
    (indexResults.sets_questions?.nbHits || 0)
  ) > 0
  const hasAnyHits = (pid, idx) => {
    const r = getLocalIndexResult(additiveResults, pid, idx)
    return !!r && r.nbHits > 0
  }
  const localAny = ['local-content', 'minisearch-content']
    .some((pid) => ['repos', 'folders', 'sets_questions'].some((idx) => hasAnyHits(pid, idx)))
  const anyEnabled = enabled.repos || enabled.folders || enabled.sets || enabled.questions
  if (!currentRefinement || (!algoliaAny && !localAny) || !anyEnabled) return null
  return (
    <React.Fragment>
      <SectionGroupHeader icon='library' label='Library' />
      {enabled.repos && <PacksGroup play={play} />}
      {enabled.folders && <FoldersGroup play={play} />}
      {enabled.sets && <SetsGroup play={play} />}
      {enabled.questions && <QuestionsGroup play={play} />}
    </React.Fragment>
  )
}

// ─── Navigation section ────────────────────────────────────────────────────────

function NavigationSection() {
  const { currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const { history } = useRouterHistory()
  const hits = getAdditiveHits(additiveResults, 'navigation')
  if (!currentRefinement || hits.length === 0) return null
  return (
    <React.Fragment>
      <SectionGroupHeader icon='compass' label='Navigation' />
      <div className='searchResults-list searchResults-list--navigation'>
        {hits.map((hit) => (
          <button
            key={`nav-${hit.objectID}`}
            type='button'
            className='searchResults-navItem'
            onClick={() => history.push(hit.path)}
          >
            <Icon name='compass' />
            <span className='searchResults-navItem-label'>{hit.label}</span>
            {hit.__localOrigin && hit.__localEngine !== 'minisearch' && (
              <Icon name='local-source' className='searchResults-localBadge' />
            )}
            {hit.__localOrigin && hit.__localEngine === 'minisearch' && (
              <Icon name='local-source-bm25' className='searchResults-localBadge searchResults-localBadge--bm25' />
            )}
          </button>
        ))}
      </div>
    </React.Fragment>
  )
}

// ─── External links section ────────────────────────────────────────────────────

function ExternalLinksSection() {
  const { currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const hits = getAdditiveHits(additiveResults, 'external-help')
  if (!currentRefinement || hits.length === 0) return null
  return (
    <React.Fragment>
      <SectionGroupHeader icon='book-open' label='Help' />
      <div className='searchResults-list searchResults-list--external'>
        {hits.map((hit) => (
          <a
            key={`ext-${hit.objectID}`}
            href={hit.url}
            target='_blank'
            rel='noopener noreferrer'
            className='searchResults-externalItem'
          >
            <Icon name='web' />
            <span className='searchResults-externalItem-label'>{hit.label}</span>
            {hit.__localOrigin && hit.__localEngine !== 'minisearch' && (
              <Icon name='local-source' className='searchResults-localBadge' />
            )}
            {hit.__localOrigin && hit.__localEngine === 'minisearch' && (
              <Icon name='local-source-bm25' className='searchResults-localBadge searchResults-localBadge--bm25' />
            )}
          </a>
        ))}
      </div>
    </React.Fragment>
  )
}

// ─── Page header ──────────────────────────────────────────────────────────────

// Count how many `sets_questions` hits across all three sources (Algolia +
// Fuse + BM25) match a given kind. Used by the results count so the figure
// reflects filter state.
function countSetsQuestionsByKind(indexResults, additiveResults, kind) {
  const isKind = (h) => getHitKind(h) === kind
  const algoliaHits = (indexResults.sets_questions && indexResults.sets_questions.hits) || []
  const fuseHits = (getLocalIndexResult(additiveResults, 'local-content', 'sets_questions')?.hits) || []
  const bm25Hits = (getLocalIndexResult(additiveResults, 'minisearch-content', 'sets_questions')?.hits) || []
  return (
    algoliaHits.filter(isKind).length +
    fuseHits.filter(isKind).length +
    bm25Hits.filter(isKind).length
  )
}

function countSimpleCategory(indexResults, additiveResults, indexName) {
  const algoliaCount = (indexResults[indexName]?.nbHits) || 0
  const fuseCount = getLocalIndexResult(additiveResults, 'local-content', indexName)?.nbHits || 0
  const bm25Count = getLocalIndexResult(additiveResults, 'minisearch-content', indexName)?.nbHits || 0
  return algoliaCount + fuseCount + bm25Count
}

function ResultsPageHeader() {
  const { indexResults, currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const { enabled, onToggle } = useCategoryFilters()

  const categoryCounts = {
    repos: countSimpleCategory(indexResults, additiveResults, 'repos'),
    folders: countSimpleCategory(indexResults, additiveResults, 'folders'),
    sets: countSetsQuestionsByKind(indexResults, additiveResults, 'set'),
    questions: countSetsQuestionsByKind(indexResults, additiveResults, 'question'),
  }
  const libraryTotal = Object.keys(categoryCounts).reduce(
    (acc, key) => acc + (enabled[key] ? categoryCounts[key] : 0), 0,
  )
  const ancillaryTotal = (
    getAdditiveHits(additiveResults, 'navigation').length +
    getAdditiveHits(additiveResults, 'external-help').length
  )
  const total = libraryTotal + ancillaryTotal

  let searchResultsText
  if (currentRefinement) {
    if (total === 1) searchResultsText = '1 Search Result'
    else if (total > 1) searchResultsText = `${total} Search Results`
    else searchResultsText = 'No Results'
  }

  const headerRow = currentRefinement ? (
    <div className='searchResults-headerRow'>
      <span className='searchResults-headerRow-count'>{searchResultsText}</span>
      <CategoryFilters enabled={enabled} onToggle={onToggle} />
    </div>
  ) : searchResultsText

  return [
    <TopPageHeader
      key='topHeader'
      location='searchResults'
      header={<SearchInput />}
      headerInfoRow={headerRow}
    />,
    <StickyPageHeader
      key='stickyHeader'
      location='searchResults'
      header={currentRefinement}
      actionGroup={headerRow}
    />,
  ]
}

// ─── SearchResultsContainer ───────────────────────────────────────────────────

class SearchResultsContainer extends Component {
  constructor(props) {
    super(props)
    this.onSearchStateChange = this.onSearchStateChange.bind(this)
    this.toggleCategory = this.toggleCategory.bind(this)
    this.state = {
      searchState: urlToSearchState(props.location),
      enabledCategories: {
        repos: true, folders: true, sets: true, questions: true,
      },
    }
  }

  componentDidMount() {
    analytics.page('Search Results')
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.location !== this.props.location) {
      this.setState({ searchState: urlToSearchState(props.location) })
    }
  }

  onSearchStateChange(searchState) {
    clearTimeout(this.debouncedSetState)
    this.debouncedSetState = setTimeout(() => {
      window.history.pushState(
        searchState,
        null,
        searchStateToUrl(this.props, searchState),
      )
    }, updateAfter)
    this.setState({ searchState })
  }

  toggleCategory(key) {
    this.setState((prev) => ({
      enabledCategories: {
        ...prev.enabledCategories,
        [key]: !prev.enabledCategories[key],
      },
    }))
  }

  render() {
    const { showSectionSelectionModal: playCb } = this.props
    const categoryFiltersValue = {
      enabled: this.state.enabledCategories,
      onToggle: this.toggleCategory,
    }
    return (
      <DocumentTitle title='Search Results - Plickers'>
        <div className='page searchResults page--wideLayout'>
          <div className='page-leftPanel' />
          <div className='page-centerPanel'>
            <SearchProviderContainer
              searchState={this.state.searchState}
              onSearchStateChange={this.onSearchStateChange}
              createURL={createURL}
              hitsPerPage={100}
            >
              <RouterHistoryBridge>
                <CategoryFiltersContext.Provider value={categoryFiltersValue}>
                  <ResultsPageHeader />
                  <div className='searchResults-content'>
                    <NavigationSection />
                    <ExternalLinksSection />
                    <LibrarySection play={playCb} />
                  </div>
                </CategoryFiltersContext.Provider>
              </RouterHistoryBridge>
            </SearchProviderContainer>
          </div>
          <div className='page-rightPanel' />
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(connect(
  (state) => ({
    permissions: state.permissions,
  }),
  { showSectionSelectionModal },
)(SearchResultsContainer))
